import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, X, Users, Hash, UserCog, Globe, AlertTriangle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { pageContainer, fadeInUp, cardReveal } from '@/lib/motion'
import { useToast } from '@/components/shared/Toast'

type RoomStatus = 'playing' | 'waiting' | 'full' | 'closed'

type MockRoom = {
  id: string
  stakes: string
  status: RoomStatus
  players: number
  maxPlayers: number
  pot: number
  hand: number
  agent: string | null
  spectators: number
}

const initialRooms: MockRoom[] = [
  { id: 'A1F3', stakes: '1/2', status: 'playing', players: 5, maxPlayers: 9, pot: 4500, hand: 47, agent: null, spectators: 8 },
  { id: 'B2G4', stakes: '5/10', status: 'playing', players: 6, maxPlayers: 6, pot: 28000, hand: 123, agent: 'Sarah_HK', spectators: 15 },
  { id: 'C3H5', stakes: '25/50', status: 'waiting', players: 2, maxPlayers: 9, pot: 0, hand: 0, agent: null, spectators: 3 },
  { id: 'D4J6', stakes: '1/2', status: 'playing', players: 8, maxPlayers: 9, pot: 1200, hand: 89, agent: 'PokerKing', spectators: 22 },
  { id: 'E5K7', stakes: '10/20', status: 'waiting', players: 1, maxPlayers: 6, pot: 0, hand: 0, agent: null, spectators: 0 },
  { id: 'F6L8', stakes: '5/10', status: 'playing', players: 4, maxPlayers: 9, pot: 8500, hand: 34, agent: null, spectators: 6 },
]

const statusConfig: Record<RoomStatus, { label: string; color: string }> = {
  playing: { label: '進行中', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' },
  waiting: { label: '等待中', color: 'bg-amber-500/15 text-amber-400 border-amber-500/25' },
  full: { label: '已滿', color: 'bg-blue-500/15 text-blue-400 border-blue-500/25' },
  closed: { label: '已關閉', color: 'bg-red-500/15 text-red-400 border-red-500/25' },
}

function formatAmount(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
}

export function AdminRoomsScreen() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [rooms, setRooms] = useState<MockRoom[]>(initialRooms)
  const [closingRoomId, setClosingRoomId] = useState<string | null>(null)
  const [closingLoading, setClosingLoading] = useState(false)

  const activeRooms = rooms.filter((r) => r.status !== 'closed')
  const closedRooms = rooms.filter((r) => r.status === 'closed')

  const summaryStats = [
    { label: '活躍', value: activeRooms.filter((r) => r.status === 'playing').length, color: 'text-emerald-400' },
    { label: '等待中', value: activeRooms.filter((r) => r.status === 'waiting').length, color: 'text-amber-400' },
    { label: '已關閉', value: closedRooms.length, color: 'text-red-400' },
    { label: '今日總手數', value: 847, color: 'text-gold' },
  ]

  const handleConfirmClose = (roomId: string) => {
    setClosingLoading(true)
    setTimeout(() => {
      setRooms((prev) =>
        prev.map((r) =>
          r.id === roomId ? { ...r, status: 'closed' as RoomStatus, players: 0, pot: 0 } : r
        )
      )
      toast({ type: 'success', title: '房間已關閉', description: `Room #${roomId} 已成功關閉` })
      setClosingRoomId(null)
      setClosingLoading(false)
    }, 500)
  }

  return (
    <motion.div
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Summary Bar */}
      <motion.div
        variants={fadeInUp}
        className="flex items-center gap-4 px-4 py-3 overflow-x-auto scrollbar-hide border-b border-foreground/[0.06]"
      >
        {summaryStats.map((stat) => (
          <div key={stat.label} className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[11px] text-muted-foreground">{stat.label}</span>
            <span className={cn('text-sm font-bold font-mono', stat.color)}>{stat.value}</span>
          </div>
        ))}
      </motion.div>

      {/* Room List */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-hide">
        {rooms.map((room) => {
          const cfg = statusConfig[room.status]
          const isClosing = closingRoomId === room.id
          const isClosed = room.status === 'closed'

          return (
            <motion.div
              key={room.id}
              variants={cardReveal}
              className={cn('glass-card rounded-2xl p-4 space-y-3', isClosed && 'opacity-50')}
            >
              {/* Row 1: ID + Stakes + Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold font-mono text-foreground">
                    Room #{room.id}
                  </span>
                  <span className="text-xs text-muted-foreground">-</span>
                  <span className="text-sm font-bold text-gold font-mono">${room.stakes}</span>
                </div>
                <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold border', cfg.color)}>
                  {cfg.label}
                </span>
              </div>

              {/* Row 2: Info Grid */}
              <div className="grid grid-cols-4 gap-2">
                <div className="flex items-center gap-1.5 p-2 rounded-lg bg-surface-elevated border border-foreground/10">
                  <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-bold font-mono text-foreground">
                    {room.players}/{room.maxPlayers}
                  </span>
                  <span className="text-[10px] text-muted-foreground">玩家</span>
                </div>

                <div className="flex items-center gap-1.5 p-2 rounded-lg bg-surface-elevated border border-foreground/10">
                  <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-mono text-foreground">
                    {room.hand > 0 ? `#${room.hand}` : '-'}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 p-2 rounded-lg bg-surface-elevated border border-foreground/10">
                  <span className="text-[10px] text-muted-foreground">彩池</span>
                  <span className={cn('text-xs font-bold font-mono', room.pot > 0 ? 'text-gold' : 'text-muted-foreground')}>
                    {room.pot > 0 ? formatAmount(room.pot) : '-'}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 p-2 rounded-lg bg-surface-elevated border border-foreground/10">
                  <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-mono text-foreground">{room.spectators}</span>
                </div>
              </div>

              {/* Row 3: Agent + Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  {room.agent ? (
                    <>
                      <UserCog className="w-3.5 h-3.5 text-gold" />
                      <span className="text-xs font-medium text-gold">{room.agent}</span>
                    </>
                  ) : (
                    <>
                      <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-foreground/5 text-muted-foreground font-bold border border-foreground/10">
                        公開
                      </span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {isClosed ? (
                    <span className="text-[11px] text-red-400 font-medium">已關閉</span>
                  ) : isClosing ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-amber-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        確定關閉此房間？
                      </span>
                      <button
                        onClick={() => handleConfirmClose(room.id)}
                        disabled={closingLoading}
                        className="px-2.5 py-1 rounded-lg bg-red-500/20 text-red-400 text-[11px] font-bold border border-red-500/30 hover:bg-red-500/30 transition-colors flex items-center gap-1"
                      >
                        {closingLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        確定
                      </button>
                      <button
                        onClick={() => setClosingRoomId(null)}
                        className="p-1 rounded-lg hover:bg-foreground/5 transition-colors"
                      >
                        <X className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => navigate(`/room/${room.id}`)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface-elevated border border-foreground/10 text-xs font-medium text-foreground hover:bg-foreground/[0.08] transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        觀戰
                      </button>
                      <button
                        onClick={() => setClosingRoomId(room.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors"
                      >
                        關閉
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>
    </motion.div>
  )
}
